import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Spin,
  Divider,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Radio,
  Space,
  Tag,
  message,
  Empty,
  Checkbox,
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { TicketIcon, GiftIcon } from "@heroicons/react/24/outline";

import {
  apiGetMe,
  apiGetCarts,
  apiPutCarts,
  apiPostOrder,
  apiGetPointPlans,
  apiPostPointOrder,
  apiGetMeWallet,
} from "api/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { errorHandler } from "utility/functions/errorHandler";
import { paymentMethod } from "utility/options/paymentMethod";
import {
  normalizePointPlan,
  getPointPlanTitle,
  ORDER_SOURCE,
} from "utility/options/pointsPlans";

import { useSelector, useDispatch } from "react-redux";
import { updateCartsAmount } from "store/slice/index";
import { useTranslation } from "react-i18next";
import { gaHandleBeginCheckout } from "components/ga/index";
import PhoneInput from "components/PhoneInput";

const PAYMENT_CHANNEL = 2; // NewebPay
const MEMBER_HISTORY_PATH = "/member?section=history&status=4";

const CheckOut = () => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const open = useSelector((state) => state.charts.isChartOpen);

  const [searchParams, setSearchParams] = useSearchParams();
  // # 帶 pointId 代表這是「購買點數方案」的獨立訂單，不經過購物車
  const pointId = searchParams.get("pointId");
  const isPointsPurchase = Boolean(pointId);

  const [radio, setRadio] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [me, setMe] = useState({});
  const [wallet, setWallet] = useState(null);
  const [carts, setCarts] = useState([]);
  const [pointPlan, setPointPlan] = useState(null);
  const [isPayLoading, setIsPayLoading] = useState(false); // # 訂單按鈕 loading
  const [checkedMap, setCheckedMap] = useState({});
  const [isAgreed, setIsAgreed] = useState(false);
  const [isUsePoint, setIsUsePoint] = useState(false);
  const [usePoint, setUsePoint] = useState(0);

  const genderOptions = [
    { value: 1, label: t("common.gender.male") },
    { value: 2, label: t("common.gender.female") },
    { value: 3, label: t("common.gender.other") },
  ];

  const isLogin =
    sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;

  // # 金額計算
  const subtotal = isPointsPurchase
    ? Number(pointPlan?.price || 0)
    : carts.reduce(
        (acc, cart) => acc + Number(cart.price || 0) * Number(cart.amount || 0),
        0,
      );

  const pointBalance = Number(wallet?.totalBalance || 0);
  const maxDeductible = Math.min(pointBalance, subtotal);
  // # 點數方案不支援錢包餘額折抵，全額由金流支付
  const canUsePoint = isLogin && !isPointsPurchase && maxDeductible > 0;
  const deduction =
    canUsePoint && isUsePoint ? Math.min(usePoint, maxDeductible) : 0;
  const payable = Math.max(subtotal - deduction, 0);
  // # 本次購買可獲得的點數（方案點數 + 加贈點數）
  const gainPoints = isPointsPurchase ? Number(pointPlan?.totalAmount || 0) : 0;

  const isEmpty = isPointsPurchase ? !pointPlan : carts.length === 0;

  // # 獲取「用戶」資訊
  const fetchMe = async () => {
    setIsLoading(true);
    try {
      const res = await apiGetMe();
      const { content } = res.data;
      setMe(content);
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  // # 獲取「錢包」餘額
  const fetchWallet = async () => {
    try {
      const res = await apiGetMeWallet();
      setWallet(res.data.content);
    } catch (err) {
      console.log("err", err);
    }
  };

  // # 獲取「購物車」資訊
  const fetchCarts = async () => {
    try {
      let details = [];
      if (isLogin) {
        const res = await apiGetCarts();
        details = res.data.content.details;
      } else {
        details = JSON.parse(localStorage.getItem("carts")) || [];
      }
      setCarts(details);
    } catch (err) {
      console.log("err", err);
    }
  };

  // # 獲取「點數方案」：Web 端只有列表 API，取回後以 id 比對
  const fetchPointPlan = async () => {
    setIsLoading(true);
    try {
      const res = await apiGetPointPlans();
      const plan = (res.data.content || [])
        .map(normalizePointPlan)
        .find((item) => String(item.id) === String(pointId));
      if (!plan) {
        message.error("找不到此點數方案，或方案已下架");
        setTimeout(() => navigate("/points", { replace: true }), 1200);
        return;
      }
      setPointPlan(plan);
    } catch (err) {
      errorHandler(err, t);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLogin) {
      fetchMe();
      fetchWallet();
    }
    // eslint-disable-next-line
  }, [sessionStorage._skyFilm_t]);

  // # 購買點數需登入；一般購票才讀購物車
  useEffect(() => {
    if (isPointsPurchase) {
      if (!isLogin) {
        navigate(
          `/login?redirect=${encodeURIComponent(`/checkout?pointId=${pointId}`)}`,
          { replace: true },
        );
        return;
      }
      fetchPointPlan();
      return;
    }
    fetchCarts();
    // eslint-disable-next-line
  }, [pointId]);

  // # 開關購物車抽屜，重新取得購物車內容
  useEffect(() => {
    if (isPointsPurchase) return;
    fetchCarts();
    // eslint-disable-next-line
  }, [open]);

  // # 當購物車數量改變，重新設置 form list數量
  useEffect(() => {
    if (isPointsPurchase) return;
    if (carts.length > 0) {
      carts.forEach((cart, index) => {
        form.setFieldValue(
          ["detail", index],
          Array.from(Array(cart.amount), () => {
            return { sourceId: cart.sourceId };
          }),
        );
      });
    }
    // eslint-disable-next-line
  }, [carts]);

  // # 購物車內容改變時，重設點數折抵上限
  useEffect(() => {
    if (!canUsePoint) {
      setIsUsePoint(false);
      setUsePoint(0);
      return;
    }
    // 只在上限變小時往下修；勾選「使用點數折抵」時才會預設填入最大值
    setUsePoint((prev) => Math.min(prev, maxDeductible));
    // eslint-disable-next-line
  }, [maxDeductible, canUsePoint]);

  // # 登出
  const logOut = () => {
    sessionStorage.removeItem("_skyFilm_t");
    setTimeout(() => navigate("/login"));
  };

  // # 移除購物車項目
  const handleRemoveItem = async (record) => {
    const nextCarts = carts.filter((item) => item.sourceId !== record.sourceId);
    try {
      if (isLogin) {
        await apiPutCarts(nextCarts);
      } else {
        localStorage.setItem("carts", JSON.stringify(nextCarts));
      }
      form.setFieldsValue({ detail: [] });
      setCarts(nextCarts);
      dispatch(updateCartsAmount(nextCarts.length));
    } catch (error) {
      errorHandler(error, t);
    }
  };

  // # 轉導金流：後端回傳 GET 轉址或 POST 表單
  const redirectToPayment = (content) => {
    if (!content || typeof content.httpMethod !== "string") {
      throw new Error("金流資訊格式錯誤");
    }

    // 全額折抵，不需要送出金流表單
    if (content.httpMethod === "") {
      message.success("付款成功");
      navigate(MEMBER_HISTORY_PATH);
      return;
    }

    if (!content.content) {
      throw new Error("金流資訊格式錯誤");
    }

    if (content.httpMethod === "GET") {
      // GET 方法：使用 navigate 轉址
      navigate(content.content);
      return;
    }

    if (content.httpMethod !== "POST") {
      throw new Error(`不支援的 HTTP 方法: ${content.httpMethod}`);
    }

    // POST 方法：使用表單提交
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content.content;
    const formEl = tempDiv.querySelector("form");

    if (!formEl) {
      throw new Error("金流表單格式錯誤");
    }

    document.body.appendChild(formEl);

    // 監聽表單提交前的錯誤
    formEl.addEventListener("error", () => {
      console.error("Form submission error");
      setIsPayLoading(false);
    });

    // 設置超時機制，如果 10 秒內沒有跳轉就釋放 loading
    const timeoutId = setTimeout(() => {
      console.warn("Payment submission timeout");
      setIsPayLoading(false);
    }, 10000);

    // 監聽頁面即將離開事件
    const handleBeforeUnload = () => {
      clearTimeout(timeoutId);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    formEl.submit();
    // 成功提交後清除事件監聽器
    setTimeout(() => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }, 1000);
  };

  // # 付款
  const handlePay = async () => {
    if (isPayLoading || isSubmittingRef.current) return;

    if (!isAgreed) {
      message.warning("請先閱讀並同意購買須知以及 T&C 條款");
      return;
    }

    // # 購買點數：一張訂單一個方案，不經過購物車
    if (isPointsPurchase) {
      if (!pointPlan) {
        message.error("找不到此點數方案，請重新選擇");
        return;
      }
      isSubmittingRef.current = true;
      setIsPayLoading(true);
      try {
        const res = await apiPostPointOrder({
          pointId: pointPlan.id,
          paymentChannel: PAYMENT_CHANNEL,
          paymentMethod: radio,
          source: ORDER_SOURCE.Point,
        });
        redirectToPayment(res.data.content);
      } catch (error) {
        console.error("Payment error:", error);
        errorHandler(error, t);
        setIsPayLoading(false);
      } finally {
        isSubmittingRef.current = false;
      }
      return;
    }

    isSubmittingRef.current = true;
    setIsPayLoading(true);

    const details = form.getFieldsValue().detail?.flat();

    // 驗證是否有訂單內容，防止產生 0 元訂單
    if (!details || details.length === 0) {
      message.error("訂單內容不得為空，請重新確認購物車項目");
      setIsPayLoading(false);
      isSubmittingRef.current = false;
      return;
    }

    try {
      const res = await apiPostOrder({
        details,
        paymentChannel: PAYMENT_CHANNEL,
        paymentMethod: radio,
        walletDiscountAmount: deduction,
      });

      // 全額折抵時沒有金流流程，後端直接回成功
      if (res.status === 200 && payable === 0) {
        message.success("付款成功");
        navigate(MEMBER_HISTORY_PATH);
        return;
      }

      redirectToPayment(res.data.content);
      gaHandleBeginCheckout({ cartItems: details });
    } catch (error) {
      console.error("Payment error:", error);
      errorHandler(error, t);
      setIsPayLoading(false); // API 錯誤時立即關閉 loading
    } finally {
      // 確保即使出錯也能重置 Ref 狀態
      isSubmittingRef.current = false;
    }
  };

  // # 付款失敗
  const handelPayError = (error) => {
    message.error(error.errorFields[0].errors[0]);
  };

  // # Radio 改變
  const onRadioChange = (e) => {
    setRadio(e.target.value);
  };

  const fillIndex = searchParams.get("fillIndex");
  const fillKey = searchParams.get("fillKey");
  const hasFilledRef = useRef(false); // 新增 flag
  const isSubmittingRef = useRef(false); // 新增防止重複送出的 flag

  // 登入後自動帶入會員資料
  useEffect(() => {
    // 條件：已登入、有 fillIndex/fillKey、會員資料已取得、還沒帶入過
    if (
      isLogin &&
      fillIndex != null &&
      fillKey != null &&
      me &&
      me.name &&
      !hasFilledRef.current
    ) {
      // 1. 勾選 checkbox
      setCheckedMap((prev) => ({
        ...prev,
        [`${fillIndex}_${fillKey}`]: true,
      }));

      // 2. 帶入會員資料
      const sourceId = form.getFieldValue([
        "detail",
        Number(fillIndex),
        Number(fillKey),
        "sourceId",
      ]);
      setTimeout(() => {
        form.setFieldValue(["detail", Number(fillIndex), Number(fillKey)], {
          sourceId,
          name: me.name,
          phone: me.phone,
          email: me.email,
        });
      }, 500);

      // 3. 標記已經帶入過
      hasFilledRef.current = true;

      // 4. 延遲一點再清除 URL 參數（確保 setCheckedMap/setFieldValue 已經執行）
      setTimeout(() => {
        searchParams.delete("fillIndex");
        searchParams.delete("fillKey");
        setSearchParams(searchParams, { replace: true });
      }, 100); // 100ms 通常就夠
    }
    // eslint-disable-next-line
  }, [me, fillIndex, fillKey]);

  // # 勾選帶入會員資料
  const handleCheck = (e, index, key) => {
    if (!isLogin) {
      // 這裡要帶上 fillIndex 和 fillKey
      navigate(`/login?redirect=/checkout&fillIndex=${index}&fillKey=${key}`);
      return;
    }
    // 記錄勾選狀態
    setCheckedMap((prev) => ({
      ...prev,
      [`${index}_${key}`]: e.target.checked,
    }));
    if (e.target.checked) {
      const { name, phone, email } = me;
      const sourceId = form.getFieldValue(["detail", index, key, "sourceId"]);
      form.setFieldValue(["detail", index, key], {
        sourceId,
        name,
        phone,
        email,
      });
    }
  };

  // # 持票人資料表單（購買點數不需填寫）
  const renderTicketDetailForm = (cart, index) => (
    <Card
      key={cart.sourceId ?? index}
      title={
        <Row gutter={16} wrap>
          <Col xs={24} sm={19} className="text-left">
            <p className="whitespace-normal">{cart.groupTitle}</p>
            <p className="whitespace-normal mobile:text-base font-semibold">
              {cart.title}
            </p>
          </Col>
          <Col xs={24} sm={5} className="flex justify-end mb-2">
            <div>
              <p className="bg-SKF-Blue-001 text-white p-2 rounded-lg">
                {t("checkOut.quantity")}：{cart.amount}
              </p>
              <p className="mt-2 text-base">NT${cart.amount * cart.price}</p>
            </div>
          </Col>
        </Row>
      }
      className="my-3 py-4 rounded-2xl [&_.ant-card-head-wrapper]:items-start"
      extra={<></>}
    >
      <Form.List name={["detail", index]}>
        {(fields) => (
          <>
            {fields.map(({ key, name }) => (
              <Row gutter={16} key={key}>
                <Col span={24}>
                  <Space className="mb-4 w-full [&_.ant-space-item:first-child]:grow">
                    <p className="text-left justify-between ">
                      {t("checkOut.tickets")} {key + 1}-{cart.amount}
                    </p>
                    <Checkbox
                      checked={checkedMap[`${index}_${key}`] || false}
                      onChange={(e) => handleCheck(e, index, key)}
                    >
                      {t("checkOut.importUser")}
                    </Checkbox>
                  </Space>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={[name, "name"]}
                    label="姓名 Name"
                    rules={[{ required: true }]}
                  >
                    <Input
                      className="p-2"
                      size="large"
                      placeholder={t("checkOut.ticketsHolderName")}
                    ></Input>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={[name, "gender"]}
                    label="性別 Gender"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={genderOptions}
                      size="large"
                      className="[&_.ant-select-selector]:p-2 [&_.ant-select-arrow]:h-0 text-left"
                      placeholder={t("checkOut.ticketsHolderGender")}
                    ></Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={[name, "age"]}
                    label="年齡 Age"
                    rules={[{ required: true }]}
                  >
                    <Input
                      className="p-2"
                      size="large"
                      placeholder={t("checkOut.ticketsHolderAge")}
                    ></Input>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={[name, "phone"]}
                    label="電話 Mobile Phone"
                    rules={[{ required: true }]}
                  >
                    <PhoneInput
                      className="p-0 [&_.ant-input]:p-2"
                      size="large"
                      placeholder={t("checkOut.ticketsHolderPhone")}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={[name, "email"]}
                    label="電子郵件 Email"
                    rules={[
                      { required: true },
                      { type: "email", message: t("common.form.emailFormat") },
                    ]}
                  >
                    <Input
                      className="p-2"
                      size="large"
                      placeholder={t("checkOut.ticketsHolderEmail")}
                    ></Input>
                  </Form.Item>
                </Col>
              </Row>
            ))}
          </>
        )}
      </Form.List>
    </Card>
  );

  // # 右側商品列：購買點數為單一方案，購票則列出購物車項目
  const renderPointPlanRow = () => (
    <Row gutter={12} align="top" wrap={false}>
      <Col flex="none">
        <div className="w-10 h-10 rounded-lg bg-SKF-G-000 flex items-center justify-center">
          <GiftIcon className="w-5 text-SKF-Blue-001" />
        </div>
      </Col>
      <Col flex="auto">
        <p className="m-0 font-semibold text-black">
          {getPointPlanTitle(pointPlan)}
        </p>
        <p className="m-0 text-SKF-G-002 text-14">
          {`SKYLINE POINT ${pointPlan.name}・共 ${pointPlan.totalAmount.toLocaleString()} 點`}
        </p>
        <Button
          type="link"
          size="small"
          className="px-0"
          onClick={() => navigate("/points")}
        >
          更換方案
        </Button>
      </Col>
      <Col flex="none" className="text-right">
        <p className="m-0 font-semibold text-black">
          NT${pointPlan.price.toLocaleString()}
        </p>
        <p className="m-0 text-SKF-G-002 text-14">x1</p>
      </Col>
    </Row>
  );

  const renderCartRow = (cart, index) => (
    <Row key={cart.sourceId ?? index} gutter={12} align="top" wrap={false}>
      <Col flex="none">
        <div className="w-10 h-10 rounded-lg bg-SKF-G-000 flex items-center justify-center">
          <TicketIcon className="w-5 text-SKF-Blue-001" />
        </div>
      </Col>
      <Col flex="auto">
        <p className="m-0 font-semibold text-black">{cart.title}</p>
        <p className="m-0 text-SKF-G-002 text-14">
          {`${cart.groupTitle}・一般票種 General Admission`}
        </p>
        <Button
          type="link"
          size="small"
          danger
          className="px-0"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(cart)}
        >
          移除
        </Button>
      </Col>
      <Col flex="none" className="text-right">
        <p className="m-0 font-semibold text-black">
          NT${(cart.price * cart.amount).toLocaleString()}
        </p>
        <p className="m-0 text-SKF-G-002 text-14">x{cart.amount}</p>
      </Col>
    </Row>
  );

  return (
    <div className="w-full bg-SKF-G-000 text-black">
      <Spin spinning={isLoading} size="large" fullscreen />
      <Form
        layout="vertical"
        form={form}
        onFinish={handlePay}
        onFinishFailed={handelPayError}
        scrollToFirstError={{ behavior: "smooth", block: "center" }}
      >
        <Row
          gutter={[24, 24]}
          className="max-w-[1280px] mx-auto px-4 py-8 desktop:px-8"
          style={{ minHeight: "calc(100vh - 64px)" }}
        >
          {/* 左側：帳戶 / 付款方式 / 持票人資料 */}
          <Col sm={{ span: 14 }} xs={{ span: 24 }} className="text-left">
            {/* 帳戶 */}
            <div className="bg-white rounded-2xl border-t-4 border-x-0 border-b-0 border-solid border-SKF-Blue-001 shadow-sm px-6 py-5 mb-4">
              <Row align="middle" gutter={12}>
                <Col flex="none">
                  <div className="w-12 h-12 rounded-full bg-SKF-Blue-001 flex items-center justify-center">
                    <UserOutlined className="text-white text-xl" />
                  </div>
                </Col>
                <Col flex="auto">
                  <Space size={8} className="mb-1">
                    <span className="font-semibold text-black">帳戶</span>
                    {isLogin && (
                      <Tag color="green" className="m-0">
                        已登入
                      </Tag>
                    )}
                  </Space>
                  <p className="m-0 text-SKF-G-002 break-all">
                    {me.email || "尚未登入"}
                  </p>
                </Col>
                <Col flex="none">
                  {isLogin ? (
                    <Button
                      onClick={logOut}
                      icon={<LogoutOutlined />}
                      className="rounded-lg"
                    >
                      {t("common.logOut")}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      className="bg-SKF-Blue-001 rounded-lg"
                      onClick={() => navigate("/login?redirect=/checkout")}
                    >
                      登入
                    </Button>
                  )}
                </Col>
              </Row>
            </div>

            {/* 付款方式 */}
            <div className="bg-white rounded-2xl shadow-sm px-6 py-5 mb-4">
              <h4 className="text-left text-black mb-0">付款 Account</h4>
              <p className="text-SKF-G-002 mb-5">{t("checkOut.transactionsInfo")}</p>
              <Radio.Group onChange={onRadioChange} value={radio}>
                <Space direction="vertical">
                  {paymentMethod
                    .filter(
                      (method) =>
                        method.value === 1 ||
                        method.value === 6 ||
                        method.value === 3,
                    )
                    .map((method) => (
                      <Radio value={method.value} key={method.id}>
                        {method.name}
                      </Radio>
                    ))}
                </Space>
              </Radio.Group>
            </div>

            {/* 持票人資料：購買點數不需填寫 */}
            {isPointsPurchase ? (
              <div className="bg-white rounded-2xl shadow-sm px-6 py-5">
                <h4 className="text-left text-black mb-0">
                  點數說明 Points Info
                </h4>
                <p className="text-SKF-G-002 mb-0 mt-2">
                  購買點數不需填寫持票人基本資料。付款完成後，方案點數與加贈點數會直接加值到
                  <span className="text-black font-semibold">
                    {me.email ? ` ${me.email} ` : "您的會員帳戶"}
                  </span>
                  ，並寄出購買確認信，點數無使用期限。
                </p>
              </div>
            ) : (
              carts.map((cart, index) => renderTicketDetailForm(cart, index))
            )}
          </Col>

          {/* 右側：訂單摘要 */}
          <Col sm={{ span: 10 }} xs={{ span: 24 }} className="text-left">
            <div className="sticky top-20">
              <div className="bg-white rounded-2xl shadow-sm px-6 py-5">
                <h4 className="text-left text-black mb-1">
                  {currentLanguage === "zhTW" && "產品"} ITEM
                </h4>
                <p className="text-SKF-G-002 text-14 mb-4">
                  {isPointsPurchase
                    ? "點數將於付款完成後加值至您的會員帳戶"
                    : "票券將寄送至您的電子郵件信箱"}
                </p>

                {isEmpty ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    className="w-full my-6 mx-0 py-10 rounded-lg border-dashed border-2 border-SKF-G-001"
                    description={
                      <div className="space-y-1">
                        <p className="m-0 font-medium">
                          {t("checkOut.noItems")}
                        </p>
                        <p className="m-0 text-sm text-SKF-G-002">
                          {t("checkOut.noItemsHint")}
                        </p>
                      </div>
                    }
                  />
                ) : (
                  <div className="flex flex-col gap-4">
                    {isPointsPurchase
                      ? renderPointPlanRow()
                      : carts.map((cart, index) => renderCartRow(cart, index))}
                  </div>
                )}

                <Divider className="my-4" />

                <Row justify="space-between" className="mb-2">
                  <Col className="text-SKF-G-003">商品小計 Subtotal</Col>
                  <Col className="text-black">
                    NT${subtotal.toLocaleString()}
                  </Col>
                </Row>

                {/* 點數折抵：購買點數方案不支援折抵，故不顯示 */}
                {canUsePoint && (
                  <div className="bg-SKF-G-000 rounded-xl px-4 py-3 my-3">
                    <Checkbox
                      checked={isUsePoint}
                      onChange={(e) => {
                        setIsUsePoint(e.target.checked);
                        if (e.target.checked) setUsePoint(maxDeductible);
                      }}
                    >
                      使用點數折抵
                    </Checkbox>
                    <p className="m-0 mt-1 text-SKF-G-002 text-14">
                      目前可用 {pointBalance.toLocaleString()} 點，本次最多可折抵{" "}
                      {maxDeductible.toLocaleString()} 點（1 點 = NT$1）
                    </p>
                    {isUsePoint && (
                      <Space.Compact className="mt-3 w-full">
                        <InputNumber
                          className="w-full"
                          min={0}
                          max={maxDeductible}
                          value={usePoint}
                          precision={0}
                          addonAfter="點"
                          onChange={(v) => setUsePoint(Number(v) || 0)}
                        />
                        <Button onClick={() => setUsePoint(maxDeductible)}>
                          全部折抵
                        </Button>
                      </Space.Compact>
                    )}
                  </div>
                )}

                {deduction > 0 && (
                  <Row justify="space-between" className="mb-2">
                    <Col className="text-SKF-G-003">點數折抵 Points</Col>
                    <Col className="text-SKF-Blue-001">
                      -NT${deduction.toLocaleString()}
                    </Col>
                  </Row>
                )}

                <div className="bg-[#EAF2FE] rounded-xl px-4 py-3 mt-3 flex items-center justify-between">
                  <span className="text-SKF-Blue-001 font-semibold">
                    結帳金額
                  </span>
                  <span className="text-black">
                    <span className="text-SKF-G-002 mr-1">NT$</span>
                    <span className="text-2xl font-bold">
                      {payable.toLocaleString()}
                    </span>
                  </span>
                </div>

                {isPointsPurchase && gainPoints > 0 && (
                  <p className="m-0 mt-3 text-SKF-G-002 text-14">
                    付款完成後可獲得{" "}
                    <span className="text-SKF-Blue-001 font-semibold">
                      {gainPoints.toLocaleString()} 點
                    </span>
                    ，點數無使用期限。
                  </p>
                )}

                <div className="border border-solid border-SKF-G-001 rounded-xl px-4 py-3 mt-4">
                  <Checkbox
                    checked={isAgreed}
                    onChange={(e) => setIsAgreed(e.target.checked)}
                  >
                    我已經閱讀並同意{" "}
                    <a
                      href="/about"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-SKF-Blue-001"
                    >
                      購票須知
                    </a>{" "}
                    以及{" "}
                    <a
                      href="/about"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-SKF-Blue-001"
                    >
                      T&C 條款
                    </a>
                  </Checkbox>
                </div>

                <Button
                  type="primary"
                  size="large"
                  className="bg-SKF-Blue-001 w-full mt-4 h-12 rounded-xl text-base font-semibold"
                  htmlType="submit"
                  loading={isPayLoading}
                  disabled={isEmpty || !isAgreed}
                >
                  結帳
                </Button>

                <p className="text-center text-SKF-Blue-001 mt-4 mb-1">
                  {t("checkOut.ticketSupport")}：support@rftexp.com
                </p>
                <p className="text-center text-SKF-G-002 m-0">
                  Skyline Film {t("checkOut.allRight")}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CheckOut;
